import type { ReferentialsResponseDto } from '@/api/WhisApi/generated/model/referentialsResponseDto';
import { buildToggleOptions } from '@/screens/ReportCreation/forms/form.utils';
import {
  buildEtiologicalAgentOptions,
  buildEtiologicalAgentOptionsByEtiologyType,
  buildEtiologyTypeOptions,
  buildWahisEtiologicalAgentOptions,
  buildWahisEtiologicalAgentOptionsByEtiologyType,
} from '@/screens/ReportCreation/forms/referentialOptions';
import type {
  FormField,
  FormLayout,
  FormOption,
  ReportFormData,
} from '@/screens/ReportCreation/reportCreation.types';
import { toNumberValue } from '@/screens/ReportCreation/utils/fieldValidation.utils';
import type { SupportedLanguage } from '@store/i18n';
import type { TFunction } from 'i18next';

const surveillanceTypeOptionConfigs = [
  {
    value: 'active',
    labelKey: 'reportCreation.step2.options.surveillanceType.active',
  },
  {
    value: 'passive',
    labelKey: 'reportCreation.step2.options.surveillanceType.passive',
  },
  {
    value: 'other',
    labelKey: 'reportCreation.step2.options.surveillanceType.other',
  },
  {
    value: 'unknown',
    labelKey: 'reportCreation.step2.options.surveillanceType.unknown',
  },
];

const yesNoUnknownOptionConfigs = [
  {
    value: 'yes',
    labelKey: 'reportCreation.step2.options.yesNoUnknown.yes',
  },
  {
    value: 'no',
    labelKey: 'reportCreation.step2.options.yesNoUnknown.no',
  },
  {
    value: 'iDontKnow',
    labelKey: 'reportCreation.step2.options.yesNoUnknown.iDontKnow',
  },
];

const yesNoOptionConfigs = [
  {
    value: 'yes',
    labelKey: 'reportCreation.step2.options.yesNo.yes',
  },
  {
    value: 'no',
    labelKey: 'reportCreation.step2.options.yesNo.no',
  },
];

const diagnosticProcedureOptionConfigs = [
  {
    value: 'yes',
    labelKey: 'reportCreation.step2.options.diagnosticProcedure.yes',
  },
  {
    value: 'no',
    labelKey: 'reportCreation.step2.options.diagnosticProcedure.no',
  },
];

const mergeFormOptions = (...optionSources: FormOption[][]): FormOption[] => {
  const optionMap = new Map<string, FormOption>(
    optionSources.flat().map((option) => [option.value, option]),
  );

  return Array.from(optionMap.values()).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
};

const mergeOptionsByType = (
  primary: Record<string, FormOption[]>,
  secondary: Record<string, FormOption[]>,
): Record<string, FormOption[]> => {
  const merged: Record<string, FormOption[]> = { ...primary };

  Object.entries(secondary).forEach(([key, options]) => {
    const existing = merged[key] ?? [];
    merged[key] = mergeFormOptions(existing, options);
  });

  return merged;
};

/**
 * Step 2: Surveillance and Etiology - Form Configuration
 */

interface Step2FormConfigOptions {
  referentials?: ReferentialsResponseDto;
  includeEtiologyFields?: boolean;
}

const buildStep2FormConfig = (
  t: TFunction,
  language: SupportedLanguage,
  { referentials, includeEtiologyFields = false }: Step2FormConfigOptions,
): FormLayout => {
  const surveillanceTypeOptions = buildToggleOptions(
    t,
    surveillanceTypeOptionConfigs,
    language,
  );

  const yesNoUnknownOptions = buildToggleOptions(
    t,
    yesNoUnknownOptionConfigs,
    language,
  );

  const yesNoOptions = buildToggleOptions(t, yesNoOptionConfigs, language);

  const diagnosticProcedureOptions = buildToggleOptions(
    t,
    diagnosticProcedureOptionConfigs,
    language,
  );

  const etiologyTypeOptions = referentials?.etiologyTypes?.length
    ? buildEtiologyTypeOptions(referentials.etiologyTypes, language)
    : [];

  const standardEtiologyAgents = referentials?.etiologicalAgents;
  const wahisEtiologyAgents = referentials?.wahisEtiologicalAgents;

  const standardEtiologyAgentOptions = standardEtiologyAgents?.length
    ? buildEtiologicalAgentOptions(standardEtiologyAgents, language)
    : [];
  const wahisEtiologyAgentOptions = wahisEtiologyAgents?.length
    ? buildWahisEtiologicalAgentOptions(wahisEtiologyAgents, language)
    : [];
  const etiologyAgentOptions = mergeFormOptions(
    standardEtiologyAgentOptions,
    wahisEtiologyAgentOptions,
  );

  const standardEtiologyAgentOptionsByType = standardEtiologyAgents?.length
    ? buildEtiologicalAgentOptionsByEtiologyType(
        standardEtiologyAgents,
        language,
      )
    : {};
  const wahisEtiologyAgentOptionsByType = wahisEtiologyAgents?.length
    ? buildWahisEtiologicalAgentOptionsByEtiologyType(
        wahisEtiologyAgents,
        language,
      )
    : {};
  const etiologyAgentOptionsByType = mergeOptionsByType(
    standardEtiologyAgentOptionsByType,
    wahisEtiologyAgentOptionsByType,
  );

  const hasDynamicEtiologyAgentOptions =
    Object.keys(etiologyAgentOptionsByType).length > 0;

  const hasEtiologyOptions =
    etiologyTypeOptions.length > 0 &&
    (hasDynamicEtiologyAgentOptions || etiologyAgentOptions.length > 0);
  const showEtiologyFields = includeEtiologyFields || hasEtiologyOptions;

  const etiologyFields: FormField<ReportFormData>[] = showEtiologyFields
    ? [
        {
          id: 'etiologyType',
          type: 'select',
          label: t('reportCreation.step2.fields.etiologyType', {
            lng: language,
          }),
          placeholder: t(
            'reportCreation.step2.placeholders.selectEtiologyType',
            { lng: language },
          ),
          required: true,
          options: etiologyTypeOptions,
          conditional: {
            dependsOn: 'knowTheCause',
            visibleWhen: 'yes',
          },
        },
        {
          id: 'etiologyAgent',
          type: 'autocomplete-search',
          label: t('reportCreation.step2.fields.etiologyAgent', {
            lng: language,
          }),
          placeholder: t(
            'reportCreation.step2.placeholders.selectEtiologyAgent',
            { lng: language },
          ),
          required: true,
          allowCustomValue: true,
          helperText: t('reportCreation.step2.helpers.etiologyAgentHint', {
            lng: language,
          }),
          options: hasDynamicEtiologyAgentOptions ? [] : etiologyAgentOptions,
          dynamicOptions: hasDynamicEtiologyAgentOptions
            ? {
                dependsOn: 'etiologyType',
                optionsByValue: etiologyAgentOptionsByType,
              }
            : undefined,
          conditional: {
            dependsOn: 'knowTheCause',
            visibleWhen: 'yes',
          },
        },
      ]
    : [];

  return {
    sections: [
      {
        title: t('reportCreation.step2.sections.surveillance', {
          lng: language,
        }),
        fields: [
          {
            id: 'surveillanceType',
            type: 'toggle-group',
            label: t('reportCreation.step2.fields.surveillanceType', {
              lng: language,
            }),
            required: true,
            options: surveillanceTypeOptions,
          },
          {
            id: 'description',
            type: 'textarea',
            placeholder: t(
              'reportCreation.step2.placeholders.describeSurveillance',
              { lng: language },
            ),
            required: false,
            maxLength: 5000,
          },
          {
            id: 'numberOfDeaths',
            type: 'numeric',
            label: t('reportCreation.step2.fields.numberOfDeaths', {
              lng: language,
            }),
            required: false,
            warnings: [
              {
                id: 'massMortality',
                dependsOn: ['numberOfDeaths'],
                isVisible: (getValue): boolean => {
                  const deaths = toNumberValue(getValue('numberOfDeaths'));

                  return (deaths ?? 0) > 1000;
                },
                renderContent: () =>
                  t('reportCreation.step2.warnings.massMortality', {
                    lng: language,
                  }),
              },
            ],
          },
          {
            id: 'numberOfSick',
            type: 'numeric',
            label: t('reportCreation.step2.fields.numberOfSick', {
              lng: language,
            }),
            required: false,
            warnings: [
              {
                id: 'massMortality',
                dependsOn: ['numberOfSick'],
                isVisible: (getValue): boolean => {
                  const sick = toNumberValue(getValue('numberOfSick'));

                  return (sick ?? 0) > 1000;
                },
                renderContent: () =>
                  t('reportCreation.step2.warnings.massMortality', {
                    lng: language,
                  }),
              },
            ],
          },
          {
            id: 'populationSize',
            type: 'numeric',
            label: t('reportCreation.step2.fields.populationSize', {
              lng: language,
            }),
            required: false,
          },
          {
            id: 'humanAffected',
            type: 'toggle-group',
            label: t('reportCreation.step2.fields.humanAffected', {
              lng: language,
            }),
            required: false,
            options: yesNoUnknownOptions,
          },
          {
            id: 'domesticAnimalAffected',
            type: 'toggle-group',
            label: t('reportCreation.step2.fields.domesticAnimalAffected', {
              lng: language,
            }),
            required: false,
            options: yesNoUnknownOptions,
          },
        ],
      },
      {
        title: t('reportCreation.step2.sections.etiology', { lng: language }),
        fields: [
          {
            id: 'knowTheCause',
            type: 'toggle-group',
            label: t('reportCreation.step2.fields.knowTheCause', {
              lng: language,
            }),
            required: true,
            options: yesNoOptions,
          },
          ...etiologyFields,
          {
            id: 'diagnosticProcedure',
            type: 'toggle-group',
            label: t('reportCreation.step2.fields.diagnosticProcedure', {
              lng: language,
            }),
            required: true,
            options: diagnosticProcedureOptions,
          },
        ],
      },
    ],
  };
};

export const getStep2FormConfig = (
  t: TFunction,
  language: SupportedLanguage,
  referentials?: ReferentialsResponseDto,
): FormLayout => buildStep2FormConfig(t, language, { referentials });

export const getStep2DetailFormConfig = (
  t: TFunction,
  language: SupportedLanguage,
  referentials?: ReferentialsResponseDto,
): FormLayout =>
  buildStep2FormConfig(t, language, {
    includeEtiologyFields: true,
    referentials,
  });
